'use client';

import { useEffect } from 'react';

interface ErrorProps {
  error: Error;
  reset: () => void;
}

export default function ErrorPage({ error, reset }: ErrorProps) {
  useEffect(() => {
    console.error('An unexpected error occurred:', error);

    // ChunkLoadError = a lazy-loaded JS chunk failed to load — usually a corrupt
    // browser cache entry (ERR_CACHE_READ_FAILURE) or a new deploy that changed
    // chunk hashes. A one-time full reload re-fetches the current HTML + chunks
    // and recovers the page instead of leaving the user stuck on "Oops".
    const message = `${error?.name ?? ''} ${error?.message ?? ''}`;
    const isChunkError = /ChunkLoadError|Loading chunk [\w-]+ failed/i.test(
      message
    );

    if (isChunkError && typeof window !== 'undefined') {
      const KEY = 'chunkReloadAt';
      const last = Number(sessionStorage.getItem(KEY) || 0);
      // Loop guard: only auto-reload if we haven't already tried in the last 10s.
      if (Date.now() - last > 10_000) {
        sessionStorage.setItem(KEY, String(Date.now()));
        window.location.reload();
      }
    }
  }, [error]);

  return (
    <div className="flex flex-col font-merriweather items-center justify-center min-h-screen px-6 text-center">
      <div className="mb-6 flex items-center justify-center">
        <h1 className="text-4xl font-bold mb-2 text-[#63544d]">Oops!</h1>
      </div>
      <h1 className="text-2xl md:text-3xl font-semibold text-gray-900 mb-3">
        Something went wrong
      </h1>
      <p className="text-gray-600 max-w-md text-center mb-6">
        An unexpected error occurred while loading this page. Please try again.
      </p>
      <button
        onClick={() => reset()}
        className="px-6 py-3 cursor-pointer rounded-full bg-[#786D67] text-white font-medium shadow hover:bg-[#5F5751] transition-all"
      >
        Try Again
      </button>
    </div>
  );
}
